import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';

export const generateMotorPrivateQuote = async (formData) => {
  try {
    // Fetch the template Excel file
    const response = await fetch('/Motor Private.xlsx');
    const arrayBuffer = await response.arrayBuffer();
    
    // Load workbook using ExcelJS (preserves all formatting)
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(arrayBuffer);
    
    // Get the first worksheet
    const worksheet = workbook.worksheets[0];

    // Calculate values
    const sumInsured = parseFloat(formData.sumInsured);
    
    // Check if TPO and use API premium, otherwise calculate with rate
    const rate = 0.03; // 3.00%
    let premium;
    if (formData.coverType === 'tpo' && formData.premiumBreakdown) {
      // For TPO, use the premium from API
      premium = formData.premiumBreakdown.basePremium;
    } else {
      // For Comprehensive, calculate with rate
      premium = sumInsured * rate;
    }
    
    const excessProtectorAmount = formData.excessProtector ? premium * 0.0025 : 0;
    const subtotal = premium + excessProtectorAmount;
    const levis = premium * 0.0045; // 0.45% of premium
    const stampDuty = 40;
    const totalAnnualPremium = subtotal + levis + stampDuty;
    
    // Use installment premium if installments are selected
    const displayPremium = (formData.premiumBreakdown && formData.premiumBreakdown.numberOfInstallments > 1) 
      ? formData.premiumBreakdown.installmentAmount 
      : totalAnnualPremium;
    const paymentLabel = (formData.premiumBreakdown && formData.premiumBreakdown.numberOfInstallments > 1)
      ? `Installment Premium (${formData.premiumBreakdown.numberOfInstallments}x)`
      : 'Total Annual Premium';

    // Format current date
    const currentDate = new Date();
    const day = currentDate.getDate();
    const daySuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    const formattedDate = `${day}${daySuffix(day)} ${monthNames[currentDate.getMonth()]}, ${currentDate.getFullYear()}`;

    // Update cells with new values (preserves all original formatting)
    worksheet.getCell('C6').value = formattedDate;
    worksheet.getCell('C8').value = formData.vehicleRegistration;
    worksheet.getCell('D8').value = sumInsured;
    worksheet.getCell('E8').value = '3.00%';
    worksheet.getCell('F8').value = premium;
    
    if (formData.excessProtector) {
      worksheet.getCell('D11').value = 'YES';
      worksheet.getCell('F11').value = excessProtectorAmount;
    } else {
      worksheet.getCell('D11').value = 'NO';
      worksheet.getCell('F11').value = 0;
    }
    
    worksheet.getCell('F24').value = subtotal;
    worksheet.getCell('F25').value = levis;
    worksheet.getCell('F26').value = stampDuty;
    worksheet.getCell('F27').value = displayPremium;
    worksheet.getCell('F31').value = displayPremium;
    worksheet.getCell('B33').value = 'Prepared By: New Digital Portal';

    // Extract additional text items from column G (EXCESS / REMARKS /CONDITIONS)
    const textItems = [];
    let startReading = false;
    for (let row = 1; row <= 100; row++) {
      const cellG = worksheet.getCell(`G${row}`);
      const cellValue = cellG.value;
      
      // Start reading after finding the header
      if (cellValue && typeof cellValue === 'string' && 
          (cellValue.includes('EXCESS') || cellValue.includes('REMARKS') || cellValue.includes('CONDITIONS'))) {
        startReading = true;
        continue;
      }
      
      // Read text items after header (skip empty cells, approval text, and standalone "Note")
      if (startReading && cellValue) {
        const text = typeof cellValue === 'string' ? cellValue.trim() : cellValue.toString().trim();
        if (text && text.length > 0 && 
            !text.toLowerCase().includes('approved by') && 
            !text.toLowerCase().includes('head of department') &&
            text.toLowerCase() !== 'note' &&
            text.toLowerCase() !== 'note:') {
          textItems.push({ row, col: 'G', text: text });
        }
      }
    }

    // Generate timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const fileName = `Motor_Private_Quote_${formData.vehicleRegistration.replace(/\s/g, '_')}_${timestamp}`;

    // Generate PDF
    await generatePDF(formData, {
      sumInsured,
      rate,
      premium,
      excessProtectorAmount,
      subtotal,
      levis,
      stampDuty,
      totalAnnualPremium,
      displayPremium,
      paymentLabel,
      timestamp,
      textItems
    }, fileName);

    return { success: true };
  } catch (error) {
    console.error('Error generating quote:', error);
    return { success: false, error: error.message };
  }
};

const generatePDF = async (formData, calculations, fileName) => {
  const doc = new jsPDF();
  
  // Load images
  const logo = await loadImage('/logo.png');
  const stamp = await loadImage('/stamp.png');
  
  // Add header background
  doc.setFillColor(15, 79, 72);
  doc.rect(0, 0, 210, 50, 'F');
  
  // Add logo on the left
  if (logo) {
    doc.addImage(logo, 'PNG', 10, 5, 60, 30);
  }
  
  // Add company info on the right
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  const rightX = 210;
  doc.text('ABSA Towers 1st floor,', rightX - 5, 10, { align: 'right' });
  doc.text('Loita Street, Nairobi, Kenya', rightX - 5, 15, { align: 'right' });
  doc.text('+254 708 247 247', rightX - 5, 20, { align: 'right' });
  doc.text('+254 788 247 247', rightX - 5, 25, { align: 'right' });
  doc.text('customerservice@definiteassurance.com', rightX - 5, 30, { align: 'right' });
  
  // Center title "Quotation"
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('QUOTATION', 105, 35, { align: 'center' });
  
  // Product name below title
  doc.setFontSize(12);
  doc.setTextColor(255, 255, 255);
  doc.text(formData.productName || 'Motor Private', 105, 42, { align: 'center' });
  
  // Draw a line separator
  doc.setDrawColor(200, 200, 200);
  doc.line(10, 48, 200, 48);

  // Reset text color
  doc.setTextColor(0, 0, 0);
  
  // Personal Information (Left side)
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('Personal Information', 20, 58);
  
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  
  const personalDetails = [
    ['Full Name:', formData.fullName || 'N/A'],
    ['Email:', formData.email || 'N/A'],
    ['Phone:', formData.phone || 'N/A'],
  ];
  
  let yPos = 65;
  personalDetails.forEach(([label, value]) => {
    doc.setFont(undefined, 'bold');
    doc.text(label, 20, yPos);
    doc.setFont(undefined, 'normal');
    doc.text(value, 48, yPos);
    yPos += 6;
  });
  
  // Vehicle Information (Right side)
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('Vehicle Information', 115, 58);
  
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  
  const vehicleDetails = [
    ['Make:', formData.vehicleMake || 'N/A'],
    ['Model:', formData.vehicleModel || 'N/A'],
    ['Year:', formData.yearOfManufacture || 'N/A'],
    ['Value:', `KES ${calculations.sumInsured.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
  ];
  
  let yPosRight = 65;
  vehicleDetails.forEach(([label, value]) => {
    doc.setFont(undefined, 'bold');
    doc.text(label, 115, yPosRight);
    doc.setFont(undefined, 'normal');
    doc.text(value, 135, yPosRight);
    yPosRight += 6;
  });
  
  yPos = Math.max(yPos, yPosRight) + 5;

  // Premium breakdown table
  doc.setFont(undefined, 'bold');
  doc.setFontSize(11);
  doc.text('Premium Breakdown', 20, yPos + 10);
  
  const tableData = [
    ['Basic Premium', `KES ${calculations.premium.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
  ];
  
  if (formData.excessProtector) {
    tableData.push(['Excess Protector (0.25%)', `KES ${calculations.excessProtectorAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`]);
  }
  
  tableData.push(
    ['Subtotal', `KES ${calculations.subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
    ['Training Levy (0.45%)', `KES ${calculations.levis.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
    ['Stamp Duty', `KES ${calculations.stampDuty.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`]
  );

  // Draw manual table
  let tableY = yPos + 15;
  const leftMargin = 20;
  const colWidth1 = 100;
  const colWidth2 = 70;
  const rowHeight = 8;
  
  // Table header - Red background
  doc.setFillColor(220, 38, 38); // Red color
  doc.rect(leftMargin, tableY, colWidth1 + colWidth2, rowHeight, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.text('Description', leftMargin + 2, tableY + 6);
  doc.text('Amount', leftMargin + colWidth1 + colWidth2 - 2, tableY + 6, { align: 'right' });
  
  // Table body
  tableY += rowHeight;
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'normal');
  
  tableData.forEach((row, index) => {
    if (index % 2 === 0) {
      doc.setFillColor(245, 245, 245);
      doc.rect(leftMargin, tableY, colWidth1 + colWidth2, rowHeight, 'F');
    }
    doc.text(row[0], leftMargin + 2, tableY + 6);
    doc.text(row[1], leftMargin + colWidth1 + colWidth2 - 2, tableY + 6, { align: 'right' });
    tableY += rowHeight;
  });
  
  // Draw table border
  doc.setDrawColor(200, 200, 200);
  doc.rect(leftMargin, yPos + 15, colWidth1 + colWidth2, (tableData.length + 1) * rowHeight);

  // Total
  const finalY = tableY + 10;
  doc.setFillColor(220, 38, 38);
  doc.rect(leftMargin, finalY, colWidth1 + colWidth2, 15, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text(calculations.paymentLabel + ':', leftMargin + 5, finalY + 10);
  doc.text(`KES ${calculations.displayPremium.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, leftMargin + colWidth1 + colWidth2 - 5, finalY + 10, { align: 'right' });

  // Footer
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.setFont(undefined, 'italic');
  doc.text('Prepared by: www.definiteassurance.com', leftMargin, finalY + 30);
  
  // Add additional text items from template
  if (calculations.textItems && calculations.textItems.length > 0) {
    doc.addPage();
    doc.setFillColor(15, 79, 72);
    doc.rect(0, 0, 210, 15, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('EXCESS / REMARKS / CONDITIONS', 105, 10, { align: 'center' });
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    let textY = 25;
    const lineHeight = 5;
    const maxWidth = 170;
    
    const drawTextWithBoldLimits = (text, x, y) => {
      if (text.includes('Limits')) {
        const parts = text.split('Limits');
        let currentX = x;
        
        parts.forEach((part, index) => {
          doc.setFont(undefined, 'normal');
          doc.text(part, currentX, y);
          currentX += doc.getTextWidth(part);
          
          if (index < parts.length - 1) {
            doc.setFont(undefined, 'bold');
            doc.text('Limits', currentX, y);
            currentX += doc.getTextWidth('Limits');
          }
        });
      } else {
        doc.setFont(undefined, 'normal');
        doc.text(text, x, y);
      }
    };
    
    calculations.textItems.forEach((item, index) => {
      // Split long text into multiple lines
      const lines = doc.splitTextToSize(item.text, maxWidth);
      lines.forEach(line => {
        if (textY > 280) {
          doc.addPage();
          textY = 20;
        }
        drawTextWithBoldLimits(line, 20, textY);
        textY += lineHeight;
      });
      textY += 2; // Extra space between items
    });

    // Add approval section at bottom of last page
    const bottomY = doc.internal.pageSize.height - 50;
    doc.setDrawColor(0);
    doc.line(20, bottomY, 190, bottomY);

    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Approved by: K. Nyamu', 20, bottomY + 10);
    doc.text('Head of Department', 20, bottomY + 18);
  }

  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text('© 2026 Definite Assurance. All rights reserved.', 105, 285, { align: 'center' });

  // Add stamp to overlap with approval section
  if (stamp) {
    doc.addImage(stamp, 'PNG', 25, doc.internal.pageSize.height - 55, 35, 25);
  }

  // Save PDF
  doc.save(`${fileName}.pdf`);
};

// Helper function to load images
const loadImage = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

const downloadFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
